import {
  ClockCircleOutlined,
  StarOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Plus, Search, X } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { type ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Flex, ScrollVFlex, Space } from '@/components/base';
import Transition from '@/components/Common/Transition';
import Modal from '@/components/Modal/Modal';
import { Stage } from '@/components/Stage/Stage';
import AMP_KEYS from '@/configs/amp-track-keys';
import type { DashboardTemplate } from '@/entities/dashboard-template';
import {
  DashboardTemplateSection,
  TemplateFormDialog,
} from '@/features/(dashboard)/dashboard-template';
import { getTemplateFormBody } from '@/features/(dashboard)/dashboard-template/templates';
import Dashboard2List from '@/features/Dashboard/components/DashboardList/DashboardList';
import DashboardModal from '@/features/Dashboard/components/DashboardModal';
import useDashboardActions from '@/features/Dashboard/hooks/useDashboardActions';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import service from '@/services/index';
import { Button, Input, PageHeader, Tabs, TabsList, TabsTrigger } from '@/shared/ui';
import { useWorkspaceCacheStoreAction } from '@/stores/WorkspaceCacheStore';
import { track } from '@/utils/track/track';

import {
  useDashboardTreeListQuery,
  useRecentViewedDashboardListQuery,
  useStarredDashboardListQuery,
} from './hooks/useDashboardListQuery';

const Dashboard2ListPage = () => {
  const color = useColor();
  const { cache } = useWorkspaceCacheStoreAction();
  const [searchParams] = useSearchParams();
  const [folderAlias, setFolderAlias] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [tab, setTab] = useWorkspaceStorage(
    'dashboard2-list-tab',
    DASHBOARD_LIST_TAB.ALL,
  );
  const { data, refetch, isLoading } = useDashboardTreeListQuery(
    folderAlias,
    searchText,
  );

  const {
    data: recentViewedDashboard,
    isLoading: isRecentViewedLoading,
    refetch: refetchRecentViewed,
  } = useRecentViewedDashboardListQuery({
    enabled: tab === DASHBOARD_LIST_TAB.RECENT,
    aliases: cache.recentDashboardHistory,
  });

  const {
    data: starredDashboard,
    isLoading: isStarredLoading,
    refetch: refetchStarred,
  } = useStarredDashboardListQuery({
    enabled: tab === DASHBOARD_LIST_TAB.STARRED,
  });

  const createDashboardModalState = useModalState();
  const createDashboardFolderModalState = useModalState();

  const openTemplateFormDialog = (template: DashboardTemplate) => {
    const FormBody = getTemplateFormBody(template.id);
    overlay.open(({ isOpen, close, unmount }) => (
      <TemplateFormDialog
        template={template}
        FormBody={FormBody}
        isOpen={isOpen}
        onClose={() => close()}
        onExitComplete={unmount}
      />
    ));
  };

  const { createDashboard } = useDashboardActions();

  const onClickCreateDashboard = async (dashboardName: string) => {
    createDashboardModalState.close();
    await createDashboard(dashboardName);
  };

  const createDashboardFolder = async (folderName: string) => {
    await service.dashboard2.createDashboardFolder(folderName);
    createDashboardFolderModalState.close();
    refetch();
  };

  useEffect(() => {
    const folderAlias = searchParams.get('folder_alias');
    if (folderAlias) {
      setFolderAlias(folderAlias);
      setSearchText('');
    } else {
      setFolderAlias(null);
      setSearchText('');
    }
  }, [searchParams]);

  return (
    <ScrollVFlex
      height="100%"
      padding="0px 0px 40px 0px"
      style={{
        backgroundColor: color.gray100,
      }}
    >
      <PageHeader
        title="Dashboard"
        desc="Visualize your insight. In your way."
        // TODO: 프로덕션에서는 isEditor 조건 복원 필요
        topRightNode={
          <Button
            size="lg"
            onClick={() => {
              track(AMP_KEYS.DASHBOARD_LIST_CREATE_DASHBOARD_CLICK);
              createDashboardModalState.open();
            }}
          >
            <Plus />
            Dashboard
          </Button>
        }
      />

      <DashboardTemplateSection onSelect={openTemplateFormDialog} />

      <Flex j="space-between" a="center" padding="0 32px">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (value) {
              setTab(value as DASHBOARD_LIST_TAB);
            }
          }}
        >
          <TabsList className="h-9 min-h-9 p-0.5">
            {DashboardListTabOptions.map((option) => (
              <TabsTrigger
                key={option.key}
                value={option.key}
                aria-label={option.tooltip}
                title={option.tooltip}
                className="min-h-8 min-w-8 px-2.5 py-0"
              >
                {option.node}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {/* TODO: 프로덕션에서는 isEditor 조건 복원 필요 */}
        <Transition status={tab === DASHBOARD_LIST_TAB.ALL}>
          <div className="flex items-center justify-end gap-2">
            <div className="relative w-full max-w-[200px]">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchText}
                onChange={(event) => setSearchText(event.currentTarget.value)}
                placeholder="Search"
                className="h-9 pr-10 pl-9"
              />
              {searchText && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="absolute top-1/2 right-2 -translate-y-1/2"
                  onClick={() => {
                    setSearchText('');
                  }}
                  aria-label="Clear search"
                >
                  <X className="size-3" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => {
                track(AMP_KEYS.DASHBOARD_LIST_CREATE_FOLDER_CLICK);
                createDashboardFolderModalState.open();
              }}
              disabled={!!folderAlias}
            >
              <Plus />
              Folder
            </Button>
          </div>
        </Transition>
      </Flex>

      <Space h={24} />
      <Stage className="flex flex-col px-10 overflow-auto flex-1">
        <Stage.Scene status={tab === DASHBOARD_LIST_TAB.ALL}>
          <Dashboard2List
            isLoading={isLoading}
            folderAlias={folderAlias}
            data={data}
            refetch={refetch}
          />
        </Stage.Scene>
        <Stage.Scene status={tab === DASHBOARD_LIST_TAB.RECENT}>
          <Dashboard2List
            isLoading={isRecentViewedLoading}
            folderAlias={null}
            data={recentViewedDashboard}
            refetch={refetchRecentViewed}
          />
        </Stage.Scene>
        <Stage.Scene status={tab === DASHBOARD_LIST_TAB.STARRED}>
          <Dashboard2List
            isLoading={isStarredLoading}
            folderAlias={null}
            data={starredDashboard}
            refetch={refetchStarred}
          />
        </Stage.Scene>
      </Stage>

      <Modal
        status={createDashboardModalState.status}
        closeModal={createDashboardModalState.close}
      >
        <DashboardModal
          onConfirm={onClickCreateDashboard}
          title="Create dashboard"
          label="Dashboard name"
          buttonLabel="Create"
        />
      </Modal>

      <Modal
        status={createDashboardFolderModalState.status}
        closeModal={createDashboardFolderModalState.close}
      >
        <DashboardModal
          onConfirm={createDashboardFolder}
          title="Create folder"
          label="Folder name"
          buttonLabel="Create"
        />
      </Modal>
    </ScrollVFlex>
  );
};

export const tableId = 'dashboard2-list-table';

enum DASHBOARD_LIST_TAB {
  ALL = 'all',
  RECENT = 'recent',
  STARRED = 'starred',
}

const DashboardListTabOptions: {
  key: DASHBOARD_LIST_TAB;
  node: ReactNode;
  tooltip: string;
}[] = [
  {
    node: <UnorderedListOutlined />,
    key: DASHBOARD_LIST_TAB.ALL,
    tooltip: 'All dashboards',
  },
  {
    node: <ClockCircleOutlined />,
    key: DASHBOARD_LIST_TAB.RECENT,
    tooltip: 'Recently viewed',
  },
  {
    node: <StarOutlined />,
    key: DASHBOARD_LIST_TAB.STARRED,
    tooltip: 'Starred',
  },
];

export default Dashboard2ListPage;
